import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Alert, ArrowLeft, ArrowRight, Button, Card, CardHeader, Edit01, PageHeader } from "@opendate-app/setlist";
import Stepper from "../shared/Stepper/Stepper";
import useFocusOnChange from "./useFocusOnChange";
import "./classesShared.css";
import "./classesWizard.css";

export function WizardCard({ title, supportingText, summary, actions, padding, className, children }) {
  const header = title
    ? (
      <CardHeader
        title={title}
        supportingText={supportingText}
        actions={actions || (summary ? <span className="classes-wizard__summary">{summary}</span> : undefined)}
      />
    )
    : undefined;
  return (
    <div className={`classes-wizard__card ${className || ""}`.trim()}>
      <Card padding={padding} header={header}>{children}</Card>
    </div>
  );
}

WizardCard.propTypes = {
  title: PropTypes.node,
  supportingText: PropTypes.node,
  summary: PropTypes.node,
  actions: PropTypes.node,
  padding: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

export function ReviewRow({ label, value }) {
  return (
    <dl className="classes-wizard__review-row">
      <dt className="classes-wizard__review-label">{label}</dt>
      <dd className="classes-wizard__review-value">{value}</dd>
    </dl>
  );
}

ReviewRow.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };

export function ReviewSection({ title, onEdit, children }) {
  return (
    <WizardCard title={title} actions={<Button variant="link-color" size="sm" icon={Edit01} onClick={onEdit} aria-label={`Edit ${title.toLowerCase()}`}>Edit</Button>}>
      {children}
    </WizardCard>
  );
}

ReviewSection.propTypes = { title: PropTypes.string.isRequired, onEdit: PropTypes.func.isRequired, children: PropTypes.node };

export function WizardFooter({ onBack, backDisabled, nextLabel, onNext, nextDisabled, finish }) {
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("conductorOrbInsetChanged"));
    return () => window.dispatchEvent(new CustomEvent("conductorOrbInsetChanged"));
  }, [nextLabel, Boolean(finish)]);

  return (
    <div className="classes-wizard__footer" data-conductor-orb-inset>
      <Button variant="secondary" icon={ArrowLeft} onClick={onBack} disabled={backDisabled}>Previous</Button>
      {finish || (
        <Button variant="primary" trailingIcon={ArrowRight} onClick={onNext} disabled={nextDisabled}>{`Next: ${nextLabel}`}</Button>
      )}
    </div>
  );
}

WizardFooter.propTypes = {
  onBack: PropTypes.func.isRequired,
  backDisabled: PropTypes.bool,
  nextLabel: PropTypes.string,
  onNext: PropTypes.func,
  nextDisabled: PropTypes.bool,
  finish: PropTypes.node,
};

export default function WizardShell({
  className, title, subtitle, cancelHref, saveLabel, onSave, errorTitle, setupTitle, wizard, nextDisabled, finish, children,
}) {
  const { errorMessage, steps, onStepClick, stepTitle, stepSubtitle } = wizard.shellProps;
  const headerActions = (
    <>
      <Button as="a" variant="secondary" href={cancelHref}>Cancel</Button>
      <Button variant="primary" onClick={onSave} aria-disabled={wizard.saving || undefined}>{wizard.saving ? "Saving…" : saveLabel}</Button>
    </>
  );
  const currentIndex = steps.findIndex((step) => step.state === "current");
  const stepTitleRef = useFocusOnChange(currentIndex);
  return (
    <div className={`od-themed classes-wizard classes-form ${className || ""}`.trim()}>
      <div className="classes-wizard__header">
        <PageHeader title={<span role="heading" aria-level="1">{title}</span>} supportingText={subtitle} actions={headerActions} />
      </div>

      {errorMessage && (
        <div className="classes-wizard__error" data-field="wizard-error" tabIndex={-1}>
          <Alert color="error" role="alert" title={errorTitle} description={errorMessage} />
        </div>
      )}

      <div className="classes-wizard__body">
        <aside className="classes-wizard__rail">
          <Card padding="md">
            <div className="classes-wizard__setup-title">{setupTitle}</div>
            <Stepper steps={steps} orientation="vertical" onStepClick={onStepClick} label={`${setupTitle} steps`} />
          </Card>
        </aside>

        <main className="classes-wizard__main">
          {currentIndex >= 0 && (
            <div className="classes-wizard__step-count">{`Step ${currentIndex + 1} of ${steps.length}`}</div>
          )}
          <h2 className="classes-wizard__step-title" tabIndex={-1} ref={stepTitleRef}>{stepTitle}</h2>
          <div className="classes-wizard__step-subtitle">{stepSubtitle}</div>
          <div className="classes-wizard__cards">{children}</div>
          <WizardFooter {...wizard.footerProps} nextDisabled={nextDisabled} finish={wizard.lastStep && finish} />
        </main>
      </div>
    </div>
  );
}

WizardShell.propTypes = {
  className: PropTypes.string,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  cancelHref: PropTypes.string.isRequired,
  saveLabel: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  errorTitle: PropTypes.node,
  setupTitle: PropTypes.string.isRequired,
  wizard: PropTypes.object.isRequired,
  nextDisabled: PropTypes.bool,
  finish: PropTypes.node,
  children: PropTypes.node,
};
